import React, { useMemo, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import styles from '../../styles/aICostTracking/CostAnalysisDashboard.module.scss';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const formatUsd = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);

const formatTokens = (value) =>
  new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value || 0);

// Professional color palette - cohesive and modern
const CHART_COLORS = {
  // Primary palette - blue spectrum
  primary: '#1E40AF',
  primaryLight: '#3B82F6',
  
  // Secondary palette - teal/green spectrum  
  secondary: '#0D9488',
  secondaryLight: '#14B8A6',
  
  // UI colors
  grid: '#F1F5F9',
  gridLine: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  background: '#FFFFFF',
  
  // Distinct, professional color palette for data visualization
  // Ordered for maximum contrast when bars are adjacent
  dataColors: [
    '#1E40AF', // Deep Blue
    '#0D9488', // Teal
    '#7C3AED', // Violet
    '#DC2626', // Red
    '#D97706', // Amber
    '#059669', // Emerald
    '#DB2777', // Pink
    '#2563EB', // Blue
    '#0891B2', // Cyan
    '#65A30D', // Lime
    '#9333EA', // Purple
    '#EA580C', // Orange
    '#0284C7', // Light Blue
    '#16A34A', // Green
    '#C026D3', // Fuchsia
    '#CA8A04', // Yellow
  ],
};

const CostAnalysisGraph = ({ costAnalysisOrgWiseData = [], selectedOrgId = '', projectUserUsageInfo = [] }) => {
  const orgChartRef = useRef(null);
  const projectChartRef = useRef(null);

  /* =========================================================
     Transform backend → chart format
     ========================================================= */

  const orgSummary = useMemo(() => {
    if (!costAnalysisOrgWiseData?.length) return { labels: [], costs: [], tokens: [] };

    const labels = costAnalysisOrgWiseData.map(row => row.organizationName || 'Unknown');
    const costs = costAnalysisOrgWiseData.map(row => Number(row.cost || 0));
    const tokens = costAnalysisOrgWiseData.map(row => Number(row.totalTokens || 0));

    return { labels, costs, tokens };
  }, [costAnalysisOrgWiseData]);

  const overallTotals = useMemo(() => {
    const totalCostUsd = orgSummary.costs.reduce((s, r) => s + r, 0);
    const totalTokens = orgSummary.tokens.reduce((s, r) => s + r, 0);
    return { totalCostUsd, totalTokens };
  }, [orgSummary]);

  /* =========================================================
     Transform project/user data for Chart.js stacked chart
     ========================================================= */

  const projectUserChartData = useMemo(() => {
    if (!projectUserUsageInfo?.length) return { categories: [], series: [], userMetadata: {} };

    const projectsMap = {};
    const usersSet = new Set();
    
    // Collect projects and users with cost > 0
    projectUserUsageInfo.forEach((row) => {
      const projectName = row.projectName || 'Unknown Project';
      const userName = row.userName || row.userEmail || 'Unknown User';
      const userEmail = row.userEmail || '';
      
      const costStr = String(row.cost || '0');
      const costUsd = parseFloat(costStr) || 0;
      
      if (costUsd > 0) {
        usersSet.add(userName);
        
        if (!projectsMap[projectName]) {
          projectsMap[projectName] = {};
        }
        
        if (!projectsMap[projectName][userName]) {
          projectsMap[projectName][userName] = {
            userName,
            userEmail,
            costUsd: 0,
            totalTokens: 0,
          };
        }
        
        projectsMap[projectName][userName].costUsd += costUsd;
        projectsMap[projectName][userName].totalTokens += Number(row.totalTokens || 0);
      }
    });

    const categories = Object.keys(projectsMap);
    const usersArray = Array.from(usersSet);
    
    // Create datasets for each user
    const datasets = usersArray.map((userName, index) => {
      const data = categories.map((projectName) => {
        const userData = projectsMap[projectName][userName];
        return userData ? userData.costUsd : 0;
      });

      const color = CHART_COLORS.dataColors[index % CHART_COLORS.dataColors.length];
      
      return {
        label: userName,
        data: data,
        backgroundColor: color,
        hoverBackgroundColor: color + 'DD',
        borderWidth: 0,
        borderRadius: 4,
        maxBarThickness: 60,
      };
    });

    // Store metadata for tooltips
    const userMetadata = {};
    categories.forEach((projectName, projectIndex) => {
      if (!userMetadata[projectIndex]) {
        userMetadata[projectIndex] = {};
      }
      usersArray.forEach((userName) => {
        const userData = projectsMap[projectName][userName];
        if (userData && userData.costUsd > 0) {
          userMetadata[projectIndex][userName] = userData;
        }
      });
    });

    return { categories, datasets, userMetadata };
  }, [projectUserUsageInfo]);

  const projectUserTotals = useMemo(() => {
    if (!projectUserUsageInfo?.length) {
      return { totalCostUsd: 0, totalTokens: 0 };
    }
    
    const totalCostUsd = projectUserUsageInfo.reduce((s, r) => {
      const cost = parseFloat(String(r.cost || '0')) || 0;
      return s + cost;
    }, 0);
    const totalTokens = projectUserUsageInfo.reduce((s, r) => s + Number(r.totalTokens || 0), 0);
    return { totalCostUsd, totalTokens };
  }, [projectUserUsageInfo]);

  /* =========================================================
     Chart.js Options - Professional unified styling
     ========================================================= */

  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 800,
      easing: 'easeOutQuart',
    },
    layout: {
      padding: {
        top: 12,
        right: 12,
        bottom: 8,
        left: 8,
      },
    },
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          color: CHART_COLORS.text,
          font: {
            size: 10,
            weight: '500',
          },
          padding: 12,
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 5,
          boxHeight: 5,
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: CHART_COLORS.background,
        titleColor: CHART_COLORS.text,
        bodyColor: CHART_COLORS.textMuted,
        borderColor: CHART_COLORS.gridLine,
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        displayColors: true,
        boxPadding: 6,
        usePointStyle: true,
        titleFont: {
          size: 12,
          weight: '600',
        },
        bodyFont: {
          size: 11,
          weight: '400',
        },
        titleSpacing: 4,
        bodySpacing: 4,
        titleMarginBottom: 8,
        caretSize: 6,
        caretPadding: 8,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: CHART_COLORS.textMuted,
          font: {
            size: 10,
            weight: '500',
          },
          padding: 8,
          maxRotation: 0,
          minRotation: 0,
        },
        border: {
          display: false,
        },
      },
      y: {
        grid: {
          color: CHART_COLORS.grid,
          drawBorder: false,
          lineWidth: 1,
        },
        ticks: {
          color: CHART_COLORS.textMuted,
          font: {
            size: 10,
            weight: '500',
          },
          padding: 10,
          maxTicksLimit: 5,
        },
        border: {
          display: false,
        },
      },
    },
  };

  // Create separate dataset for each organization to show in legend
  const orgWiseChartData = useMemo(() => {
    if (!orgSummary.labels.length) return { labels: [], datasets: [] };
    
    return {
      labels: orgSummary.labels,
      datasets: orgSummary.labels.map((orgName, index) => {
        const color = CHART_COLORS.dataColors[index % CHART_COLORS.dataColors.length];
        return {
          label: orgName,
          data: orgSummary.labels.map((_, i) => i === index ? orgSummary.costs[i] : 0),
          backgroundColor: color,
          hoverBackgroundColor: color + 'DD',
          borderRadius: 6,
          maxBarThickness: 60,
          categoryPercentage: 0.8,
          barPercentage: 0.9,
        };
      }),
    };
  }, [orgSummary]);

  const orgWiseChartOptions = {
    ...commonOptions,
    plugins: {
      ...commonOptions.plugins,
      title: {
        display: false,
      },
      legend: {
        ...commonOptions.plugins.legend,
        display: true,
      },
      tooltip: {
        ...commonOptions.plugins.tooltip,
        callbacks: {
          title: function(context) {
            return context[0].label;
          },
          label: function(context) {
            const datasetIndex = context.datasetIndex;
            const cost = orgSummary.costs[datasetIndex];
            const tokens = orgSummary.tokens[datasetIndex];
            
            if (context.parsed.y === 0) return null;
            
            return [
              `Cost: ${formatUsd(cost)}`,
              `Tokens: ${formatTokens(tokens)}`,
            ];
          },
          filter: function(tooltipItem) {
            return tooltipItem.parsed.y > 0;
          },
        },
      },
    },
    scales: {
      ...commonOptions.scales,
      x: {
        ...commonOptions.scales.x,
        grid: {
          display: false,
        },
        ticks: {
          ...commonOptions.scales.x.ticks,
          maxRotation: 30,
          minRotation: 0,
          font: {
            size: 10,
            weight: '500',
          },
          padding: 6,
          callback: function(value, index) {
            const label = this.getLabelForValue(value);
            // Allow more characters before truncating
            return label.length > 18 ? label.substring(0, 15) + '...' : label;
          },
        },
      },
      y: {
        ...commonOptions.scales.y,
        beginAtZero: true,
        grid: {
          ...commonOptions.scales.y.grid,
          drawTicks: false,
        },
        ticks: {
          ...commonOptions.scales.y.ticks,
          callback: function(value) {
            if (value >= 1000) return '$' + (value / 1000).toFixed(1) + 'k';
            if (value >= 1) return '$' + value.toFixed(0);
            return '$' + value.toFixed(2);
          },
        },
      },
    },
  };

  const projectUserChartOptions = {
    ...commonOptions,
    plugins: {
      ...commonOptions.plugins,
      legend: {
        ...commonOptions.plugins.legend,
        display: true,
      },
      tooltip: {
        ...commonOptions.plugins.tooltip,
        callbacks: {
          title: function(context) {
            const projectIndex = context[0].dataIndex;
            return projectUserChartData.categories[projectIndex];
          },
          label: function(context) {
            const projectIndex = context.dataIndex;
            const userName = context.dataset.label;
            const cost = context.parsed.y;
            
            if (cost === 0) return null;
            
            const userMeta = projectUserChartData.userMetadata[projectIndex]?.[userName];
            if (!userMeta) return null;

            return [
              `${userName}`,
              `Cost: ${formatUsd(cost)}`,
              `Tokens: ${formatTokens(userMeta.totalTokens)}`,
            ];
          },
        },
      },
    },
    scales: {
      ...commonOptions.scales,
      x: {
        ...commonOptions.scales.x,
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          ...commonOptions.scales.x.ticks,
          maxRotation: 30,
          minRotation: 0,
          font: {
            size: 10,
            weight: '500',
          },
          padding: 6,
          callback: function(value) {
            const label = this.getLabelForValue(value);
            // Allow more characters for project names
            return label.length > 18 ? label.substring(0, 15) + '...' : label;
          },
        },
      },
      y: {
        ...commonOptions.scales.y,
        stacked: true,
        beginAtZero: true,
        grid: {
          ...commonOptions.scales.y.grid,
          drawTicks: false,
        },
        ticks: {
          ...commonOptions.scales.y.ticks,
          callback: function(value) {
            if (value >= 1000) return '$' + (value / 1000).toFixed(1) + 'k';
            if (value >= 1) return '$' + value.toFixed(0);
            return '$' + value.toFixed(2);
          },
        },
      },
    },
  };

  const projectUserChartDataForChart = {
    labels: projectUserChartData.categories,
    datasets: projectUserChartData.datasets,
  };

  /* =========================================================
     Render
     ========================================================= */
  return (
    <div className={styles.chartsGrid}>
      {/* ================= ORG WISE CHART ================= */}
      <div className={styles.chartGridItem}>
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <div className={styles.chartHeaderLeft}>
              <span className={styles.chartIcon}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 3v18h18"/>
                  <rect x="7" y="10" width="3" height="8" rx="1"/>
                  <rect x="14" y="6" width="3" height="12" rx="1"/>
                </svg>
              </span>
              <h3 className={styles.chartTitleText}>Organization Cost</h3>
            </div>
            <div className={styles.chartHeaderRight}>
              <div className={styles.statBadge}>
                <span className={styles.statLabel}>Total</span>
                <span className={styles.statValue}>{formatUsd(overallTotals.totalCostUsd)}</span>
              </div>
              <div className={styles.statBadgeMuted}>
                <span className={styles.statLabel}>Tokens</span>
                <span className={styles.statValueMuted}>{formatTokens(overallTotals.totalTokens)}</span>
              </div>
            </div>
          </div>

          <div className={styles.chartBody}>
            {orgSummary.labels.length > 0 ? (
              <Bar ref={orgChartRef} data={orgWiseChartData} options={orgWiseChartOptions} />
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M3 3v18h18" strokeLinecap="round"/>
                    <path d="M7 14v3m4-6v6m4-9v9" strokeLinecap="round"/>
                  </svg>
                </div>
                <p className={styles.emptyStateText}>No organization data available</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ================= PROJECT-WISE STACKED CHART ================= */}
      <div className={styles.chartGridItem}>
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <div className={styles.chartHeaderLeft}>
              <span className={styles.chartIcon}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                </svg>
              </span>
              <h3 className={styles.chartTitleText}>Project Cost by User</h3>
            </div>
            <div className={styles.chartHeaderRight}>
              {selectedOrgId ? (
                <>
                  <div className={styles.statBadge}>
                    <span className={styles.statLabel}>Total</span>
                    <span className={styles.statValue}>{formatUsd(projectUserTotals.totalCostUsd)}</span>
                  </div>
                  <div className={styles.statBadgeMuted}>
                    <span className={styles.statLabel}>Tokens</span>
                    <span className={styles.statValueMuted}>{formatTokens(projectUserTotals.totalTokens)}</span>
                  </div>
                </>
              ) : (
                <span className={styles.selectHint}>Select organization</span>
              )}
            </div>
          </div>

          <div className={styles.chartBody}>
            {selectedOrgId && projectUserChartData.categories.length > 0 ? (
              <Bar ref={projectChartRef} data={projectUserChartDataForChart} options={projectUserChartOptions} />
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 6v6l4 2"/>
                  </svg>
                </div>
                <p className={styles.emptyStateText}>
                  {selectedOrgId 
                    ? 'No project data available for this organization'
                    : 'Select an organization to view project breakdown'
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostAnalysisGraph;
